import { Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';

import { Callout } from '@/components/notifications/Callout/Callout';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import { MultiDocumentUploaderWithListV2 } from '@/modules/documents/MultiDocumentUploader/MultiDocumentUploaderWithListV2';

import { DocumentValuePropCards } from './components/DocumentValuePropCards';
import { RecommendedDocumentsPanel } from './components/RecommendedDocumentsPanel';
import { useDocumentUploadStepContext } from './context/documentUploadStep.context';
import { DOCUMENT_UPLOAD_COPY } from './documentUpload.constants';

// The body content sits 140px from the modal edge (Figma). The wizard's body
// wrapper already adds 64px of horizontal padding, so add the remaining 76px
// here for a consistent 140px total regardless of viewport width.
const CONTENT_INSET_PX = 140 - 64;
const HEADER_MAX_WIDTH = 840;
const DOCUMENT_CONTENT_MAX_WIDTH = 910;

/**
 * Body for the "Documents" step. Two states, keyed off whether any document has
 * been uploaded this session:
 * - Landing: top-aligned title + subtitle, the blue dropzone, the
 *   recommendations toggle, and the three value-prop cards.
 * - Post-upload: top-aligned/scrollable, title only, dropzone, recommendations,
 *   and the list of uploaded documents with their (inferred) types.
 */
export function DocumentUploadBody() {
  const { householdId, expandRecommendations } = useDocumentUploadStepContext();
  const { uploadedFiles, isUploading } = useMultiDocumentUploaderContext();
  const hasUploads = uploadedFiles.length > 0;

  // Auto-expand the "Recommended documents" checklist the first time an upload
  // starts, so the user sees what else to gather. One-time (ref-guarded) so a
  // later upload never re-opens a panel the user has since collapsed.
  const hasAutoExpandedRecommendationsRef = useRef(false);
  useEffect(() => {
    if (isUploading && !hasAutoExpandedRecommendationsRef.current) {
      hasAutoExpandedRecommendationsRef.current = true;
      expandRecommendations();
    }
  }, [isUploading, expandRecommendations]);

  // The household is created by step 1 before this step is reachable; guard
  // only so the uploader always has a concrete householdId.
  if (!householdId) return null;

  const uploadedCountLabel = `${uploadedFiles.length} ${
    uploadedFiles.length === 1 ? 'document' : 'documents'
  } uploaded`;

  return (
    <Stack
      spacing={3}
      minHeight="100%"
      alignItems="center"
      justifyContent="flex-start"
      sx={{ px: `${CONTENT_INSET_PX}px` }}
    >
      <Stack spacing={1} width="100%" maxWidth={HEADER_MAX_WIDTH}>
        <Typography variant="title-l" textAlign="center">
          {DOCUMENT_UPLOAD_COPY.title}
        </Typography>
        {!hasUploads && (
          <Typography variant="body-m" textAlign="center">
            {DOCUMENT_UPLOAD_COPY.subtitle}
          </Typography>
        )}
      </Stack>

      <Stack spacing={1.5} width="100%" maxWidth={DOCUMENT_CONTENT_MAX_WIDTH}>
        <MultiDocumentUploaderWithListV2
          householdId={householdId}
          appearance="accent"
          // 'compact' in both states: the onboarding dropzone never shows the
          // document-stack illustration (which only renders in 'expanded').
          variant="compact"
          uploaderSx={{ height: 96, justifyContent: 'center' }}
          // Wider document-type select and tighter 8px row gap per design QA.
          documentTypeSelectWidth={320}
          uploadedListSpacing={1}
          autoScrollToUploadedFiles={false}
          belowUploaderContent={
            <>
              <RecommendedDocumentsPanel showActions={!hasUploads} />
              {hasUploads && (
                <Typography variant="title-s">{uploadedCountLabel}</Typography>
              )}
              {uploadedFiles.length > 25 && (
                <Callout severity="warning">
                  {DOCUMENT_UPLOAD_COPY.uploadWarning}
                </Callout>
              )}
            </>
          }
        />
        {!hasUploads && (
          <Stack pt={4} width="100%" alignSelf="center">
            <DocumentValuePropCards />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
